import axios from 'axios';
import moment from 'moment';
import { Geolocation } from '@capacitor/geolocation';

// Used for to populate door state on initial load and reset door state on logout
const getDoorDefaultState = () => {
  return {
		doorToOpenReaderId: null, // Used for saveDoorOpenAccessLog
		doorToOpenRangedBeacons: null, // Used for saveDoorOpenAccessLog
		doorAndLocationData: null,
		doorDataSelectedLocationId: null,
		userAccessRights: null,
		userCurrentCordinates: null,
		locationWarningShown: false,
		userAnswerToLocationAuthorizationDenied: false,
		successfulLocationValidation: { timestamp: null, cordinates: null, beacon: null, locationId: null },
		doorOpenFailures: [], // {timestamp: null, locationId: null}
		beaconsInRanging: null,
		iBeaconDelegate: null,
		lastSuccessfulLocationConfirmation: { timestamp: null, position: null, locationId: null },
		currentWatchedPosition: null,
		locationWatchId: null,
  }
}

export default {
	namespaced: true,
	state: getDoorDefaultState(),
	getters: {
		recentValidLocationConfirmation: (state, getters, rootState) => {
			const confirmation = state.lastSuccessfulLocationConfirmation;
			if (!confirmation || !confirmation.timestamp || !confirmation.position) {
				return null;
			}

			const settings = rootState.common.settings;
			const reuseMinutes = settings.reuseLocationConfirmationLastMinutes || 0;
			if (reuseMinutes <= 0) {
				return null;
			}

			const confirmationAge = Date.now() - confirmation.timestamp;
			const maxAge = reuseMinutes * 60 * 1000;

			if (confirmationAge > maxAge) {
				return null;
			}

			return confirmation;
		},
		recentWatchedPosition: (state) => {
			const position = state.currentWatchedPosition;
			if (!position || !position.timestamp) {
				return null;
			}

			const positionAge = Date.now() - position.timestamp;
			const maxAge = 30 * 1000;

			if (positionAge > maxAge) {
				return null;
			}

			return position;
		}
	},
	mutations: {
		mutateDoorStateToDefault(state) {
			Object.assign(state, getDoorDefaultState())
		},
		mutateDoorToOpenReaderId(state, doorToOpenReaderId) {
			state.doorToOpenReaderId = doorToOpenReaderId;
		},
		mutateDoorToOpenRangedBeacons(state, doorToOpenRangedBeacons) {
			state.doorToOpenRangedBeacons = doorToOpenRangedBeacons;
		},
		mutateDoorAndLocationData(state, doorAndLocationData) {
			state.doorAndLocationData = doorAndLocationData;
		},
		mutateDoorDataSelectedLocationId(state, doorDataSelectedLocationId) {
			state.doorDataSelectedLocationId = doorDataSelectedLocationId;
		},
		mutateUserCurrentCordinates(state, userCurrentCordinates) {
			state.userCurrentCordinates = userCurrentCordinates;
		},
		mutateLocationWarningShown(state, locationWarningShown) {
			state.locationWarningShown = locationWarningShown;
		},
		mutateUserAnswerToLocationAuthorizationDenied(state, userAnswerToLocationAuthorizationDenied) {
			state.userAnswerToLocationAuthorizationDenied = userAnswerToLocationAuthorizationDenied;
		},
		mutateSuccessfulLocationValidation(state, beaconAndLocationId) {
			state.successfulLocationValidation = {
				timestamp: Date.now() / 1000 | 0, // Set timestamp in seconds
				cordinates: state.userCurrentCordinates?.coords ? state.userCurrentCordinates.coords : null,
				beacon: beaconAndLocationId.beacon,
				locationId: beaconAndLocationId.locationId
			}
		},
		mutateDoorOpenFailures(state, locationId) {
			if (locationId === null) {
				// Reset failures
				state.doorOpenFailures = []
			} else {
				// Add failure to array
				state.doorOpenFailures.push(
					{
						timestamp: Date.now() / 1000 | 0, // Set timestamp in seconds
						locationId
					}
				)
			}
		},
		mutateUserbeaconsInRanging(state, beaconsInRanging) {
			state.beaconsInRanging = beaconsInRanging;
		},
		mutateIBeaconDelegate(state, iBeaconDelegate) {
			state.iBeaconDelegate = iBeaconDelegate;
		},
		mutateUserAccessRights(state, userAccessRights) {
			state.userAccessRights = userAccessRights;
		},
		mutateLastSuccessfulLocationConfirmation(state, confirmation) {
			state.lastSuccessfulLocationConfirmation = confirmation;
		},
		clearLastSuccessfulLocationConfirmation(state) {
			state.lastSuccessfulLocationConfirmation = null;
		},
		mutateCurrentWatchedPosition(state, position) {
			state.currentWatchedPosition = position;
		},
		mutateLocationWatchId(state, watchId) {
			state.locationWatchId = watchId;
		},
		clearWatchedPosition(state) {
			state.currentWatchedPosition = null;
			state.locationWatchId = null;
		},
	},
	actions: {
		async getDoorAndLocationDataFromServer({ rootGetters, commit }) {
			try {
				const { data } = await axios.get(`${rootGetters["common/activeHost"].ajaxUrl}?controller=ajax&getaccesscontrolappdata=1&appauth=${rootGetters["common/activeHost"].appauth}`)
				commit('mutateDoorAndLocationData', data)

				// If door data has only one location set locationId
				if (data.locations.length === 1) {
					commit('mutateDoorDataSelectedLocationId', data.locations[0].locationId)
				}
				// console.log(data)
				return data
			} catch (error) {
				return Promise.reject(error)
			}
		},
		async getPosition({ commit}) {
			try {
				const coordinates = await Geolocation.getCurrentPosition({ enableHighAccuracy:true });
				commit('mutateUserCurrentCordinates', coordinates)
			} catch (error) {
				console.error("getPosition()", error.message)
			}
		},
		async getUserAccessRights({ rootGetters, commit }) {
			try {
				const { data } = await axios.get(`${rootGetters["common/activeHost"].restUrl}accessrights/`)

				if (typeof data === 'object' && data.rows.length) {
					const accessRights = [];

					data.rows.forEach(item =>
						accessRights.push({
							productName: item.productName || null,
							usableQuantity: item.usableQuantity || null,
							validFrom: item.validFrom ? moment(item.validFrom).startOf('day') : null,
							validTo: item.validTo ? moment(item.validTo).endOf('day') : null,
							orderId: item.orderId || null,
							futureValidFound: (item.validFrom && moment(item.validFrom).isAfter(moment(), 'day') &&
								(!item.validTo || moment(item.validTo).isAfter(moment(), 'day'))),
							continuousFound: ((!item.validFrom || moment(item.validFrom).isSameOrBefore(moment(), 'day')) && !item.validTo),
							currentlyValidFound: ((!item.validFrom || moment(item.validFrom).isSameOrBefore(moment(), 'day')) &&
								(!item.validTo || moment(item.validTo).isSameOrAfter(moment(), 'day'))),
							errorStatus: item.errorStatus,
							name: item.name || null
						})
					)

					commit('mutateUserAccessRights', accessRights)
				}
			} catch (error) {
				return Promise.reject(error)
			}
		},
	}
}